import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import { AuthProvider } from '@/lib/auth'
import { OfflineProvider } from '@/lib/offline/context'
import { ThemeProvider, useTheme } from '@/lib/theme'
import { ApiError } from '@/lib/api'
import { TooltipProvider } from '@/components/ui'
import { router } from './routes'
import './index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 20_000,
      refetchOnWindowFocus: true,
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
    },
  },
})

// App-shell caching for outages (production only; the dev server uses hot reload instead).
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => void navigator.serviceWorker.register(`/sw.js?v=${__BUILD_ID__}`).catch(() => undefined))
}

const subscribeToPath = (fn: (pathname: string) => void) => router.subscribe((state) => fn(state.location.pathname))

function ThemedToaster() {
  const { theme } = useTheme()
  return <Toaster position="top-right" richColors closeButton theme={theme} toastOptions={{ className: 'font-sans' }} />
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <OfflineProvider>
          <ThemeProvider subscribe={subscribeToPath}>
            <TooltipProvider>
              <RouterProvider router={router} />
              <ThemedToaster />
            </TooltipProvider>
          </ThemeProvider>
        </OfflineProvider>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
)
